import { FC, useState } from 'react';
import { Box, Chip, Divider, IconButton, LinearProgress, Popover, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans, useTrans } from '@libs/i18n';
import { Assessment } from '@libs/types';
import { InfoIcon, TargetIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { DomainResultViewModel, RadarComparisonEntry } from '../../types';

// ── SVG geometry constants ────────────────────────────────────────────────────
// ViewBox is sized to fit content snugly so the SVG has minimal internal
// whitespace. Heights are tuned to fit up to 3-line labels at top/bottom with
// enough vertical clearance from the adjacent side labels.
const VW = 740;
const VH = 600;
const CX = VW / 2;
const CY = 300;
const LEVELS = 5;
// Extra clearance for the center-top and center-bottom labels so they don't
// crowd the upper-side / lower-side neighbours that sit at nearby angles.
const TOP_BOTTOM_GAP = 16;
const SIDE_LINE_GAP  = 3;

// Per-slot colors for baseline overlays (current always uses primary)
const OVERLAY_COLORS = ['#F59E0B', '#10B981', '#EF4444', '#8B5CF6'] as const;

type LabelPosition = 'top' | 'bottom' | 'right' | 'left';

function angle(i: number, n: number) { return -90 + (i / n) * 360; }

function polar(deg: number, r: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

function buildPolygon(values: number[], n: number, r: number, activeIndices: number[]) {
  return activeIndices
    .map((idx) => {
      const { x, y } = polar(angle(idx, n), (Math.max(0, values[idx]) / 100) * r);
      return `${x},${y}`;
    })
    .join(' ');
}

/** Classify a label's position on the perimeter so anchoring is consistent. */
function classifyPosition(a: number): LabelPosition {
  const n = ((a % 360) + 360) % 360;
  // n=270 is straight up (top), n=90 is straight down (bottom).
  if (n >= 250 && n <= 290) return 'top';
  if (n >= 70 && n <= 110) return 'bottom';
  if (n < 70 || n > 290) return 'right';
  return 'left';
}

function anchorFor(pos: LabelPosition): 'start' | 'middle' | 'end' {
  if (pos === 'top' || pos === 'bottom') return 'middle';
  return pos === 'right' ? 'start' : 'end';
}

function dxFor(pos: LabelPosition): number {
  if (pos === 'right') return 10;
  if (pos === 'left') return -10;
  return 0;
}

/** Anchor offset of the first line, relative to the (x,y) label point.
 * - top:    block grows upward, with its bottom row sitting TOP_BOTTOM_GAP px
 *           above the axis tip so the center-top label clears the upper-side
 *           labels that share a similar vertical band.
 * - bottom: block grows downward, with its top row sitting TOP_BOTTOM_GAP px
 *           below the axis tip for the same reason in mirror.
 * - side:   block is centered on the axis line.
 */
function firstLineDy(pos: LabelPosition, maxLines: number, lineHeight: number): number {
  const blockSpan = (maxLines - 1) * lineHeight;
  if (pos === 'top')    return -TOP_BOTTOM_GAP - blockSpan;
  if (pos === 'bottom') return TOP_BOTTOM_GAP + SIDE_LINE_GAP;
  return SIDE_LINE_GAP - blockSpan / 2;
}

/** Split a name into rows of up to WORDS_PER_LINE words. */
function wrapByWords(name: string, wordsPerLine: number): string[] {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return [' '];
  const words = trimmed.split(/\s+/);
  const lines: string[] = [];
  for (let i = 0; i < words.length; i += wordsPerLine) {
    lines.push(words.slice(i, i + wordsPerLine).join(' '));
  }
  return lines;
}

/** Pad a label's lines to maxLines so every label occupies the same visual
 * block. Empty slots are placed on the side that's hidden by the anchoring:
 * above for top labels (text settles at bottom of block), below for bottom
 * labels, and split evenly for side labels (centered text).
 */
function padToMax(lines: string[], maxLines: number, pos: LabelPosition): string[] {
  const need = maxLines - lines.length;
  if (need <= 0) return lines;
  const blank = ' '; // non-breaking space — invisible but keeps line height
  if (pos === 'top')    return [...Array<string>(need).fill(blank), ...lines];
  if (pos === 'bottom') return [...lines, ...Array<string>(need).fill(blank)];
  const padTop = Math.floor(need / 2);
  return [
    ...Array<string>(padTop).fill(blank),
    ...lines,
    ...Array<string>(need - padTop).fill(blank),
  ];
}

/** Helper to calculate the connection point of the leader line on the pill boundary */
function getLeaderEndPoint(tipX: number, tipY: number, pillX: number, pillY: number, pillW: number, pillH: number) {
  const pillCenterX = pillX + pillW / 2;
  const pillCenterY = pillY + pillH / 2;
  const dx = pillCenterX - tipX;
  const dy = pillCenterY - tipY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return { x: pillCenterX, y: pillCenterY };

  const ux = dx / dist;
  const uy = dy / dist;

  const absUx = Math.abs(ux) > 0.0001 ? Math.abs(ux) : 0.0001;
  const absUy = Math.abs(uy) > 0.0001 ? Math.abs(uy) : 0.0001;
  const r_boundary = Math.min((pillW / 2) / absUx, (pillH / 2) / absUy);

  return {
    x: pillCenterX - ux * (r_boundary + 3), // 3px gap
    y: pillCenterY - uy * (r_boundary + 3),
  };
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface DomainRadarChartProps {
  domainResults: DomainResultViewModel[];
  activeVersion?: Assessment;
  /** All sibling versions to show in the version picker chips */
  allVersions?: Assessment[];
  /** Controlled: which version IDs are currently overlaid (max 3) */
  selectedVersionIds: string[];
  /** Fired when user clicks a version chip to toggle it */
  onVersionToggle: (versionId: string) => void;
  /** Pre-fetched comparison data for each selected version */
  radarComparisons: RadarComparisonEntry[];
  height?: number;
  // Dynamic tuning variables
  chartRadius?: number;
  labelRadius?: number;
  fontSize?: number;
  lineHeight?: number;
  wordsPerLine?: number;
}

// ── Version chips (shared between radar and fallback list) ────────────────────

interface VersionChipsProps {
  activeVersion?: Assessment;
  otherVersions: Assessment[];
  selectedVersionIds: string[];
  onVersionToggle: (versionId: string) => void;
  maxOverlays: number;
}

const VersionChips: FC<VersionChipsProps> = observer(({
  activeVersion,
  otherVersions,
  selectedVersionIds,
  onVersionToggle,
  maxOverlays,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const primaryColor = theme.palette.primary.main;

  if (otherVersions.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      <Chip
        label={activeVersion
          ? t('assessments.overview.charts.versionCurrent', { version: activeVersion.version })
          : t('assessments.overview.charts.versionCurrent', { version: '' })}
        size="small"
        sx={{
          fontSize: '0.72rem', fontWeight: 600,
          backgroundColor: primaryColor, color: 'white',
          border: `1px solid ${primaryColor}`,
          width: '100%',
          justifyContent: 'center',
        }}
      />

      {otherVersions.map((v) => {
        const slotIdx = selectedVersionIds.indexOf(v.id);
        const isSelected = slotIdx !== -1;
        const color = isSelected ? OVERLAY_COLORS[slotIdx] : undefined;
        const canAdd = !isSelected && selectedVersionIds.length < maxOverlays;

        return (
          <Chip
            key={v.id}
            label={`V${v.version}`}
            size="small"
            onClick={() => {
              if (isSelected || canAdd) onVersionToggle(v.id);
            }}
            disabled={!isSelected && !canAdd}
            sx={{
              fontSize: '0.72rem', fontWeight: 500, cursor: (isSelected || canAdd) ? 'pointer' : 'default',
              backgroundColor: isSelected ? color : theme.palette.grey[100],
              color: isSelected ? 'white' : theme.palette.text.secondary,
              border: `1px solid ${isSelected ? color : theme.palette.grey[300]}`,
              width: '100%',
              justifyContent: 'center',
              '&.Mui-disabled': { opacity: 0.45 },
              '&:hover': (isSelected || canAdd) ? {
                backgroundColor: isSelected ? color : theme.palette.grey[200],
                opacity: 0.9,
              } : {},
            }}
          />
        );
      })}

      {selectedVersionIds.length >= maxOverlays && (
        <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', mt: 0.75, textAlign: 'center', fontSize: '0.68rem', px: 0.25 }}>
          {t('assessments.overview.radar.maxOverlays', { count: maxOverlays })}
        </Typography>
      )}
    </Box>
  );
});

// ── Fallback bar gauge list (used when n < 3) ─────────────────────────────────

interface FallbackGaugeListProps {
  applicable: DomainResultViewModel[];
  activeVersion?: Assessment;
  radarComparisons: RadarComparisonEntry[];
  height: number;
}

const FallbackGaugeList: FC<FallbackGaugeListProps> = observer(({
  applicable,
  activeVersion,
  radarComparisons,
  height,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const primaryColor = theme.palette.primary.main;

  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        px: 2,
        py: 2,
        overflow: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
          {t('assessments.overview.charts.radar.needMoreDomains')}
        </Typography>
        <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
          {t('assessments.overview.charts.radar.needMoreDomainsSubtitle', { count: applicable.length })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {applicable.map((d) => {
          const currentScore = d.isSkipped ? 0 : Math.round(d.maturityScore ?? 0);
          return (
            <Box key={d.domainId} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                  <Typography variant="textSM_regular" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {d.domainName}
                  </Typography>
                  {d.isSkipped && (
                    <Typography variant="textXS_regular" sx={{ color: theme.palette.warning.dark, fontStyle: 'italic' }}>
                      {t('assessments.overview.charts.notAssessedSuffix')}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="textSM_semiBold"
                  sx={{ color: d.isSkipped ? theme.palette.text.disabled : primaryColor, flexShrink: 0 }}
                >
                  {currentScore}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={currentScore}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[100],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: primaryColor,
                    borderRadius: 1,
                  },
                }}
              />

              {/* Overlay version bars */}
              {radarComparisons.map((comp, ci) => {
                const color = OVERLAY_COLORS[ci] ?? OVERLAY_COLORS[0];
                const score = Math.round(comp.scoresByDomainId.get(d.domainId) ?? 0);
                return (
                  <Box key={comp.version.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                    <Typography variant="textXS_regular" sx={{ color: 'text.secondary', minWidth: 32 }}>
                      V{comp.version.version}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={score}
                      sx={{
                        flex: 1,
                        height: 5,
                        borderRadius: 1,
                        backgroundColor: theme.palette.grey[100],
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: color,
                          borderRadius: 1,
                        },
                      }}
                    />
                    <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', minWidth: 32, textAlign: 'right' }}>
                      {score}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2.5, mt: 'auto', pt: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 20, height: 4, backgroundColor: primaryColor, borderRadius: 1 }} />
          <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
            {activeVersion
              ? t('assessments.overview.charts.versionCurrent', { version: activeVersion.version })
              : t('assessments.overview.charts.versionCurrent', { version: '' })}
          </Typography>
        </Box>
        {radarComparisons.map((comp, ci) => {
          const color = OVERLAY_COLORS[ci] ?? OVERLAY_COLORS[0];
          return (
            <Box key={comp.version.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 20, height: 4, backgroundColor: color, borderRadius: 1 }} />
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
                V{comp.version.version}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

export const DomainRadarChart: FC<DomainRadarChartProps> = observer(
  ({
    domainResults,
    activeVersion,
    allVersions,
    selectedVersionIds,
    onVersionToggle,
    radarComparisons,
    height,
    chartRadius: R = 220,
    labelRadius: LR = 237,
    fontSize: FONT = 12,
    lineHeight: LINE_HEIGHT = 15,
    wordsPerLine: WORDS_PER_LINE = 2,
  }) => {
    const theme = useTheme();
    const { t } = useTrans();
    const [hovered, setHovered] = useState<number | null>(null);
    const [hiddenDomainIds, setHiddenDomainIds] = useState<Set<string>>(new Set());
    const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

    const infoPopover = (
      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxWidth: 280, p: 2, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: `1px solid ${theme.palette.grey[200]}` } } }}
      >
        <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 1 }}>
          <Trans i18nKey="assessments.overview.charts.howToRead" />
        </Typography>
        <Divider sx={{ mb: 1.25, borderColor: 'grey.100' }} />
        {[
          { label: 'Polygon shape', desc: 'Each axis represents a compliance domain. The further out a point reaches, the higher that domain\'s readiness score.' },
          { label: 'Filled area', desc: 'The shaded polygon shows your overall coverage. A larger, rounder shape means more balanced readiness across all domains.' },
          { label: 'Axis labels', desc: 'Click a domain label to hide/show it from the polygon for focused comparison.' },
          { label: 'Version overlays', desc: 'Select past versions from the sidebar to overlay their polygons and visualize progress over time.' },
        ].map(({ label, desc }) => (
          <Box key={label} sx={{ mb: 1.25 }}>
            <Typography variant="textXS_semiBold" sx={{ color: 'text.primary', display: 'block' }}>{label}</Typography>
            <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>{desc}</Typography>
          </Box>
        ))}
      </Popover>
    );

    // Include skipped domains too — they score at 0 on the radar so the user
    // sees every applicable domain (assessed and missing), ranked by axis order.
    const inScope = domainResults.filter((d) => d.isApplicable);

    // Sort by orderIndex ASC so that the domains are ordered clockwise
    // as they come from the API.
    const applicable = [...inScope].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
    );
    const otherVersions = (allVersions ?? []).filter((v) => v.id !== activeVersion?.id);
    const maxOverlays = 3;
    const cardHeight = height ?? 560;

    // Toggle hidden domain filter
    const handleLabelClick = (domainId: string) => {
      setHiddenDomainIds((prev) => {
        const next = new Set(prev);
        if (next.has(domainId)) {
          next.delete(domainId);
        } else {
          next.add(domainId);
        }
        return next;
      });
    };

    // Empty state — no applicable domains at all
    if (applicable.length === 0) {
      return (
        <Box
          sx={{
            height: cardHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: theme.palette.grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.disabled,
              mb: 0.5,
            }}
          >
            <TargetIcon size={22} />
          </Box>
          <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
            {t('assessments.overview.charts.empty.title')}
          </Typography>
          <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', maxWidth: 280 }}>
            {t('assessments.overview.charts.empty.subtitle')}
          </Typography>
        </Box>
      );
    }

    // Radar needs at least 3 axes to draw a meaningful polygon. With 1 or 2
    // applicable domains we render a bar-gauge list instead.
    if (applicable.length < 3) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
          <Box
            sx={{
              width: 180,
              flexShrink: 0,
              borderRight: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2.25,
              backgroundColor: '#ffffff',
            }}
          >
            {/* Title & Description */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.25 }}>
                  {t('assessments.overview.radar.title') || 'Domain Coverage Radar'}
                </Typography>
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', lineHeight: 1.35, fontSize: '0.72rem' }}>
                  {radarComparisons.length > 0
                    ? t('assessments.overview.radar.subtitleComparing') || 'Maturity coverage comparing selected versions'
                    : t('assessments.overview.radar.subtitle') || 'Maturity coverage across all compliance domains'}
                </Typography>
              </Box>
              <IconButton size="small" onClick={(e) => setInfoAnchor(e.currentTarget)} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' }, mt: -0.25 }}>
                <InfoIcon size={16} />
              </IconButton>
            </Box>
            {infoPopover}

            {otherVersions.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'grey.100' }} />

                {/* Versions selector */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="textXS_semiBold" sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                    {t('assessments.overview.charts.versions') || 'Versions'}
                  </Typography>
                  <VersionChips
                    activeVersion={activeVersion}
                    otherVersions={otherVersions}
                    selectedVersionIds={selectedVersionIds}
                    onVersionToggle={onVersionToggle}
                    maxOverlays={maxOverlays}
                  />
                </Box>
              </>
            )}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <FallbackGaugeList
              applicable={applicable}
              activeVersion={activeVersion}
              radarComparisons={radarComparisons}
              height={cardHeight}
            />
          </Box>
        </Box>
      );
    }

    const n = applicable.length;
    const currentScores = applicable.map((d) =>
      d.isSkipped ? 0 : Math.round(d.maturityScore ?? 0),
    );

    // Filter active indices for polygon drawing
    const activeIndices = applicable
      .map((d, i) => (hiddenDomainIds.has(d.domainId) ? -1 : i))
      .filter((idx) => idx !== -1);

    // Adapt typography and geometry slightly if we have a lot of domains.
    // Thanks to the 1D vertical relaxation loop, labels are prevented from overlapping,
    // which allows us to keep the chart radius (adjustedR) and the label radius (adjustedLR)
    // close to each other, maintaining a tight, premium spacing.
    const adjustedFONT = n > 5 ? Math.max(9.0, FONT - (n - 5) * 0.3) : FONT;
    const adjustedLINE_HEIGHT = n > 5 ? Math.max(11.0, LINE_HEIGHT - (n - 5) * 0.4) : LINE_HEIGHT;
    const adjustedR = n > 8 ? Math.max(200, R - (n - 8) * 1.2) : R;
    const adjustedLR = adjustedR + 36;

    // Wrap every label and pad to the longest, so all labels render with the
    // same number of visual rows regardless of name length. Full names are kept
    // (no truncation) — the pill widens to fit.
    const labelLinesRaw = applicable.map((d) => wrapByWords(d.domainName ?? '', WORDS_PER_LINE));
    const maxLines = Math.max(1, ...labelLinesRaw.map((l) => l.length));

    // 1. Initialize adjusted labels list with layout boundaries and properties
    const adjustedLabels = applicable.map((d, i) => {
      const a = angle(i, n);
      const { x: x_orig, y: y_orig } = polar(a, adjustedLR);
      const pos = classifyPosition(a);
      const anchor = anchorFor(pos);
      const dx = dxFor(pos);
      const dy0 = firstLineDy(pos, maxLines, adjustedLINE_HEIGHT);
      const lines = padToMax(labelLinesRaw[i], maxLines, pos);

      // Use raw wrapped lines for width calculation to avoid counting trailing blank padding
      const longestLine = Math.max(...labelLinesRaw[i].map((l) => l.trim().length));
      const pillW = Math.max(40, longestLine * (adjustedFONT * 0.58)) + 14;
      const pillH = maxLines * adjustedLINE_HEIGHT + 10;

      const pillX = anchor === 'middle'
        ? x_orig + dx - pillW / 2
        : anchor === 'start'
        ? x_orig + dx - 6
        : x_orig + dx - pillW + 6;

      const pillY_orig = y_orig + dy0 - adjustedLINE_HEIGHT + 2;

      return {
        index: i,
        domainId: d.domainId,
        domainName: d.domainName,
        isSkipped: d.isSkipped,
        x_orig,
        y_orig,
        pillX_orig: pillX,
        pillX,
        y_orig_pill: pillY_orig,
        y: pillY_orig, // current pill Y (top of pill)
        pillW,
        pillH,
        anchor,
        dx,
        dy0,
        lines,
        pos,
        angle: a,
      };
    });

    // Run vertical relaxation to resolve any overlapping label pills
    const ITERATIONS = 120;
    for (let iter = 0; iter < ITERATIONS; iter++) {
      // 1. Pull back to original Y coordinates to keep them close to their axes
      for (let i = 0; i < adjustedLabels.length; i++) {
        const li = adjustedLabels[i];
        li.y += (li.y_orig_pill - li.y) * 0.06;
      }

      // 2. Push overlapping pills apart vertically
      for (let i = 0; i < adjustedLabels.length; i++) {
        for (let j = i + 1; j < adjustedLabels.length; j++) {
          const li = adjustedLabels[i];
          const lj = adjustedLabels[j];

          const overlapX = li.pillX + li.pillW > lj.pillX && li.pillX < lj.pillX + lj.pillW;
          const overlapY = li.y + li.pillH > lj.y && li.y < lj.y + lj.pillH;

          if (overlapX && overlapY) {
            const val = Math.min(li.y + li.pillH, lj.y + lj.pillH) - Math.max(li.y, lj.y);
            const shift = val / 2 + 1;
            if (li.y < lj.y) {
              li.y -= shift;
              lj.y += shift;
            } else {
              li.y += shift;
              lj.y -= shift;
            }
          }
        }
      }

      // 3. Update pillX based on current y to prevent overlapping the radar chart circle
      for (let i = 0; i < adjustedLabels.length; i++) {
        const li = adjustedLabels[i];
        if (li.pos === 'right' || li.pos === 'left') {
          const dyCenter = Math.abs((li.y + li.pillH / 2) - CY);
          const circleW = Math.sqrt(Math.max(0, adjustedR * adjustedR - dyCenter * dyCenter));
          const minDistance = 14;
          if (li.pos === 'right') {
            const minX = CX + circleW + minDistance;
            li.pillX = Math.max(li.pillX_orig, minX);
          } else {
            const maxX = CX - circleW - minDistance - li.pillW;
            li.pillX = Math.min(li.pillX_orig, maxX);
          }
        }
      }

      // 4. Constrain to viewBox bounds and keep top/bottom labels outside the web
      for (let i = 0; i < adjustedLabels.length; i++) {
        const li = adjustedLabels[i];
        const verticalGap = 8;
        
        if (li.pos === 'top') {
          const maxY = CY - adjustedR - verticalGap - li.pillH;
          const minY = 6;
          li.y = Math.max(minY, li.y);
          if (maxY >= minY) {
            li.y = Math.min(li.y, maxY);
          }
        } else if (li.pos === 'bottom') {
          const minY = CY + adjustedR + verticalGap;
          const maxY = VH - 6 - li.pillH;
          li.y = Math.max(minY, li.y);
          li.y = Math.min(li.y, maxY);
        } else {
          // Side labels: standard viewBox constraints
          const minY = 6;
          const maxY = VH - 6 - li.pillH;
          li.y = Math.max(minY, Math.min(maxY, li.y));
        }
      }
    }

    const primaryColor = theme.palette.primary.main;
    const gridColor    = '#D1D5DB'; // Crisp, visible grey
    const axisColor    = '#9CA3AF'; // Darker grey for axis lines
    const hoverHighlight = theme.palette.primary.main;

    const hoveredDomain = hovered !== null ? applicable[hovered] : null;
    const isHoveredDomainHidden = hoveredDomain ? hiddenDomainIds.has(hoveredDomain.domainId) : false;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
        {/* Left Sidebar: Title, Subtitle, and Version Selection */}
        <Box
          sx={{
            width: 180,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2.25,
            backgroundColor: '#ffffff',
          }}
        >
          {/* Title & Description */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.25 }}>
                {t('assessments.overview.radar.title') || 'Domain Coverage Radar'}
              </Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', lineHeight: 1.35, fontSize: '0.72rem' }}>
                {radarComparisons.length > 0
                  ? t('assessments.overview.radar.subtitleComparing') || 'Maturity coverage comparing selected versions'
                  : t('assessments.overview.radar.subtitle') || 'Maturity coverage across all compliance domains'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={(e) => setInfoAnchor(e.currentTarget)} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' }, mt: -0.25 }}>
              <InfoIcon size={16} />
            </IconButton>
          </Box>
          {infoPopover}

          {otherVersions.length > 0 && (
            <>
              <Divider sx={{ borderColor: 'grey.100' }} />

              {/* Versions selector */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="textXS_semiBold" sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                  {t('assessments.overview.charts.versions') || 'Versions'}
                </Typography>
                <VersionChips
                  activeVersion={activeVersion}
                  otherVersions={otherVersions}
                  selectedVersionIds={selectedVersionIds}
                  onVersionToggle={onVersionToggle}
                  maxOverlays={maxOverlays}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Right Area: SVG Chart (no legend at bottom).
            Pinned LTR so SVG text-anchor positions stay consistent in an RTL
            (Arabic) layout — otherwise the tooltip title/values anchor from
            the wrong edge and render outside the card. */}
        <NoFlipBox
          mode="ltr"
          sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg
            viewBox={`0 0 ${VW} ${VH}`}
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: 'block', overflow: 'visible' }}
          >
            <defs>
              <radialGradient id="radar-bg" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={theme.palette.grey[50] as string} stopOpacity="0.5" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
              <radialGradient id="radar-polygon-fill" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={primaryColor} stopOpacity="0.45" />
                <stop offset="85%" stopColor={primaryColor} stopOpacity="0.25" />
                <stop offset="100%" stopColor={primaryColor} stopOpacity="0.05" />
              </radialGradient>
              <filter id="radar-point-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="radar-polygon-shadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="6" stdDeviation="10"
                  floodColor={primaryColor} floodOpacity="0.20" />
              </filter>
            </defs>

            {/* Soft background disc */}
            <circle cx={CX} cy={CY} r={adjustedR} fill="url(#radar-bg)" />

            {/* Concentric layered level backgrounds (Subtle banded web styling) */}
            {Array.from({ length: LEVELS }).map((_, lvl) => {
              const lvlR = ((lvl + 1) / LEVELS) * adjustedR;
              return (
                <polygon
                  key={`lvl-bg-${lvl}`}
                  points={Array.from({ length: n }, (__, i) => {
                    const { x, y } = polar(angle(i, n), lvlR);
                    return `${x},${y}`;
                  }).join(' ')}
                  fill={lvl % 2 === 0 ? 'rgba(0, 0, 0, 0.015)' : 'transparent'}
                  pointerEvents="none"
                />
              );
            })}

            {/* Grid rings */}
            {Array.from({ length: LEVELS }).map((_, lvl) => {
              const lvlR = ((lvl + 1) / LEVELS) * adjustedR;
              const isOutermost = lvl === LEVELS - 1;
              return (
                <polygon
                  key={lvl}
                  points={Array.from({ length: n }, (__, i) => {
                    const { x, y } = polar(angle(i, n), lvlR);
                    return `${x},${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke={gridColor}
                  strokeWidth={isOutermost ? 1.5 : 1}
                  strokeDasharray={isOutermost ? '0' : '4 4'}
                  strokeOpacity={isOutermost ? 1.0 : 0.8}
                />
              );
            })}

            {/* Grid scale labels — placed in the gap between the top axis and
                its clockwise neighbour, with white pills for legibility. */}
            {(() => {
              // Angle that sits halfway between axis 0 (-90°) and axis 1, so the
              // labels never collide with a domain axis line.
              const labelAngle = -90 + (180 / n);
              return Array.from({ length: LEVELS }).map((_, lvl) => {
                const lvlR = ((lvl + 1) / LEVELS) * adjustedR;
                const pct  = ((lvl + 1) / LEVELS) * 100;
                const pos  = polar(labelAngle, lvlR);
                const txt  = `${pct}%`;
                const pillW = txt.length * 6.5 + 10;
                return (
                  <g key={`scale-${lvl}`}>
                    <rect
                      x={pos.x - pillW / 2}
                      y={pos.y - 9}
                      width={pillW}
                      height={16}
                      rx={4}
                      fill="white"
                      fillOpacity={0.95}
                      stroke={gridColor}
                      strokeWidth={0.5}
                      style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.05))' }}
                    />
                    <text
                      x={pos.x}
                      y={pos.y + 3}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight={600}
                      fill={theme.palette.text.secondary as string}
                    >
                      {txt}
                    </text>
                  </g>
                );
              });
            })()}

            {/* Hover highlight wedge slice — draws attention to active slice segment */}
            {hovered !== null && !isHoveredDomainHidden && (() => {
              const a = angle(hovered, n);
              const leftAngle = a - (180 / n);
              const rightAngle = a + (180 / n);
              const p0 = polar(leftAngle, adjustedR);
              const p1 = polar(rightAngle, adjustedR);
              return (
                <path
                  d={`M ${CX} ${CY} L ${p0.x} ${p0.y} A ${adjustedR} ${adjustedR} 0 0 1 ${p1.x} ${p1.y} Z`}
                  fill={primaryColor}
                  opacity={0.04}
                  pointerEvents="none"
                  style={{ transition: 'all 0.2s ease' }}
                />
              );
            })()}

            {/* Axis lines */}
            {applicable.map((d, i) => {
              const tip = polar(angle(i, n), adjustedR);
              const isHov = hovered === i;
              const isHidden = hiddenDomainIds.has(d.domainId);
              return (
                <line key={i} x1={CX} y1={CY} x2={tip.x} y2={tip.y}
                  stroke={isHov && !isHidden ? hoverHighlight : axisColor}
                  strokeWidth={isHov && !isHidden ? 1.5 : 1}
                  strokeOpacity={isHidden ? 0.12 : isHov ? 0.8 : 0.95}
                  strokeDasharray={isHidden ? '3 3' : undefined}
                />
              );
            })}

            {/* Hover highlight line axis guide */}
            {hovered !== null && !isHoveredDomainHidden && (() => {
              const a = angle(hovered, n);
              const tip = polar(a, adjustedR);
              return (
                <line
                  x1={CX} y1={CY} x2={tip.x} y2={tip.y}
                  stroke={hoverHighlight}
                  strokeWidth={2}
                  strokeOpacity={0.85}
                  strokeDasharray="4 3"
                />
              );
            })()}

            {/* Overlay polygons (baselines) — rendered before current so current is on top */}
            {activeIndices.length >= 3 && radarComparisons.map((comp, ci) => {
              const color = OVERLAY_COLORS[ci] ?? OVERLAY_COLORS[0];
              const scores = applicable.map((d) =>
                Math.round(comp.scoresByDomainId.get(d.domainId) ?? 0),
              );
              return (
                <polygon
                  key={comp.version.id}
                  points={buildPolygon(scores, n, adjustedR, activeIndices)}
                  fill={`${color}12`}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                />
              );
            })}

            {/* Current polygon — gradient fill + soft drop-shadow so the shape
                reads clearly against the grid even at low scores. */}
            {activeIndices.length >= 3 && (
              <polygon
                points={buildPolygon(currentScores, n, adjustedR, activeIndices)}
                fill="url(#radar-polygon-fill)"
                stroke={primaryColor}
                strokeWidth={2.5}
                strokeLinejoin="round"
                strokeLinecap="round"
                filter="url(#radar-polygon-shadow)"
              />
            )}

            {/* Data points — current (Dual-ring styling: white fill, color border, outer halo on hover) */}
            {applicable.map((d, i) => {
              const isHidden = hiddenDomainIds.has(d.domainId);
              if (isHidden) return null;

              const { x, y } = polar(angle(i, n), (currentScores[i] / 100) * adjustedR);
              const isHov = hovered === i;
              const isMissing = d.isSkipped;
              return (
                <g key={i}>
                  {/* Hover Ripple Halo */}
                  <circle
                    cx={x}
                    cy={y}
                    r={isHov ? 9 : 0}
                    fill={primaryColor}
                    opacity={0.25}
                    style={{ transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', pointerEvents: 'none' }}
                  />
                  {/* Point Glow */}
                  {isHov && (
                    <circle cx={x} cy={y} r={12}
                      fill={primaryColor}
                      opacity={0.15}
                      filter="url(#radar-point-glow)"
                    />
                  )}
                  {/* Main Dual-Ring Point */}
                  <circle
                    cx={x} cy={y}
                    r={isHov ? 5.5 : 4}
                    fill={isMissing ? theme.palette.grey[400] : '#ffffff'}
                    stroke={isMissing ? theme.palette.grey[500] : primaryColor}
                    strokeWidth={2}
                    style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                  />
                </g>
              );
            })}

            {/* Leader lines from axis tip to label — keeps the label/axis link
                visually obvious now that every label sits at the same radius. */}
            {adjustedLabels.map((lbl) => {
              const isHidden = hiddenDomainIds.has(lbl.domainId);
              const tip = polar(lbl.angle, adjustedR);
              const endPoint = getLeaderEndPoint(tip.x, tip.y, lbl.pillX, lbl.y, lbl.pillW, lbl.pillH);
              const isHov = hovered === lbl.index;
              return (
                <line
                  key={`leader-${lbl.index}`}
                  x1={tip.x} y1={tip.y}
                  x2={endPoint.x} y2={endPoint.y}
                  stroke={isHov && !isHidden ? hoverHighlight : axisColor}
                  strokeWidth={1}
                  strokeOpacity={isHidden ? 0.08 : isHov ? 0.7 : 0.3}
                  strokeDasharray={isHidden ? '5 5' : isHov ? '0' : '3 3'}
                />
              );
            })}

            {/* Labels — uniform radius, clickable pill backgrounds, toggle active state on click */}
            {adjustedLabels.map((lbl) => {
              const isHidden = hiddenDomainIds.has(lbl.domainId);
              const isHov = hovered === lbl.index;
              const textX = lbl.pillX + lbl.pillW / 2;

              return (
                <g
                  key={lbl.index}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLabelClick(lbl.domainId)}
                  onMouseEnter={() => setHovered(lbl.index)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {/* Clickable pill background (rendered for all states) */}
                  <rect
                    x={lbl.pillX}
                    y={lbl.y}
                    width={lbl.pillW}
                    height={lbl.pillH}
                    rx={10}
                    fill={
                      !isHidden
                        ? isHov
                          ? 'rgba(59, 130, 246, 0.06)'
                          : '#ffffff'
                        : isHov
                        ? theme.palette.grey[200]
                        : theme.palette.grey[50]
                    }
                    stroke={
                      !isHidden
                        ? isHov
                          ? primaryColor
                          : theme.palette.primary.light
                        : isHov
                        ? theme.palette.grey[400]
                        : theme.palette.grey[300]
                    }
                    strokeWidth={isHov ? 1.5 : 1}
                    strokeDasharray={isHidden ? '3 3' : undefined}
                    style={{
                      filter: !isHidden && isHov ? 'drop-shadow(0 4px 10px rgba(0,0,0,0.05))' : 'none',
                      transition: 'all 0.15s ease',
                    }}
                  />
                  <text
                    textAnchor="middle"
                    fontSize={adjustedFONT}
                    fontWeight={isHov ? 700 : 500}
                    fill={
                      !isHidden
                        ? isHov
                          ? (theme.palette.text.primary as string)
                          : (theme.palette.text.secondary as string)
                        : (theme.palette.text.disabled as string)
                    }
                    style={{ transition: 'fill 0.15s' }}
                  >
                    {lbl.lines.map((line, li) => {
                      // Mathematically exact vertical centering of the text block inside the pill height
                      const textY = lbl.y + (lbl.pillH - (maxLines - 1) * adjustedLINE_HEIGHT) / 2 + 0.225 * adjustedFONT + li * adjustedLINE_HEIGHT;
                      return (
                        <tspan
                          key={li}
                          x={textX}
                          y={textY}
                        >
                          {line}
                        </tspan>
                      );
                    })}
                  </text>
                </g>
              );
            })}

            {/* Hover tooltip — anchored inside the radar so it never clips */}
            {hoveredDomain && hovered !== null && !isHoveredDomainHidden && (() => {
              const a = angle(hovered, n);
              const { x: tx, y: ty } = polar(a, adjustedR * 0.45);
              const rowCount = 1 + radarComparisons.length;

              // Show the full domain name (no ellipsis) and widen the card to
              // fit it. ~6.7px per uppercase glyph at fontSize 10 + 0.5px
              // letter-spacing, plus 14px padding on each side.
              const titleCharW = 10 * 0.62 + 0.5;
              const titleText = (hoveredDomain.domainName ?? '').toUpperCase();
              const titleW = titleText.length * titleCharW + 28;
              // Grow from the 210 baseline to fit the title, capped to the
              // viewBox width so the card itself never spills outside the chart.
              const boxW = Math.min(Math.max(210, titleW), VW - 12);
              const boxH = 34 + rowCount * 20 + 8;
              const bx = Math.min(Math.max(tx - boxW / 2, 6), VW - boxW - 6);
              const by = Math.min(Math.max(ty - boxH / 2, 6), VH - boxH - 6);

              return (
                <g pointerEvents="none" style={{ transition: 'all 0.15s ease' }}>
                  {/* Shadowed Tooltip Card */}
                  <rect x={bx} y={by} width={boxW} height={boxH} rx={12}
                    fill="white" stroke="#E5E7EB" strokeWidth={1}
                    style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.08))' }}
                  />
                  {/* Tooltip Header Pill background */}
                  <path d={`M ${bx} ${by+12} A 12 12 0 0 1 ${bx+12} ${by} L ${bx+boxW-12} ${by} A 12 12 0 0 1 ${bx+boxW} ${by+12} L ${bx+boxW} ${by+34} L ${bx} ${by+34} Z`} fill="#F9FAFB" />
                  
                  {/* Tooltip Header Title */}
                  <text x={bx + 14} y={by + 22} fontSize={10} fontWeight={700} letterSpacing="0.5px"
                    fill={theme.palette.text.secondary as string}>
                    {titleText}
                  </text>
                  
                  <line x1={bx} y1={by+34} x2={bx+boxW} y2={by+34} stroke="#E5E7EB" strokeWidth={1} />

                  {/* Current version row */}
                  <circle cx={bx + 18} cy={by + 52} r={4.5} fill={primaryColor} />
                  <text x={bx + 28} y={by + 56} fontSize={11} fontWeight={500}
                    fill={theme.palette.text.secondary as string}>
                    {activeVersion
                      ? t('assessments.overview.charts.versionCurrent', { version: activeVersion.version })
                      : 'Current'}
                  </text>
                  <text x={bx + boxW - 14} y={by + 56} fontSize={11} fontWeight={700} textAnchor="end"
                    fill={hoveredDomain.isSkipped
                      ? (theme.palette.text.disabled as string)
                      : (theme.palette.text.primary as string)}>
                    {currentScores[hovered]}%
                    {hoveredDomain.isSkipped ? ` (${t('assessments.overview.charts.notAssessedSuffix')})` : ''}
                  </text>

                  {/* Overlay versions rows */}
                  {radarComparisons.map((comp, ci) => {
                    const color = OVERLAY_COLORS[ci] ?? OVERLAY_COLORS[0];
                    const score = Math.round(comp.scoresByDomainId.get(applicable[hovered].domainId) ?? 0);
                    const rowY = by + 52 + (ci + 1) * 20;
                    return (
                      <g key={comp.version.id}>
                        <circle cx={bx + 18} cy={rowY} r={4.5} fill={color} />
                        <text x={bx + 28} y={rowY + 4} fontSize={11} fontWeight={500}
                          fill={theme.palette.text.secondary as string}>
                          V{comp.version.version}
                        </text>
                        <text x={bx + boxW - 14} y={rowY + 4} fontSize={11} fontWeight={700} textAnchor="end"
                          fill={theme.palette.text.primary as string}>
                          {score}%
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })()}
          </svg>
        </NoFlipBox>
      </Box>
    );
  },
);
